class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.boundClose === 'true') return;
    overlay.dataset.boundClose = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // Rebind when Theme Editor / section reload replaces <cart-drawer>.
    if (cartLink._egjCartDrawer === this) return;

    if (cartLink._egjCartClick) {
      cartLink.removeEventListener('click', cartLink._egjCartClick);
    }
    if (cartLink._egjCartKeydown) {
      cartLink.removeEventListener('keydown', cartLink._egjCartKeydown);
    }

    cartLink._egjCartDrawer = this;
    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink._egjCartClick = (event) => {
      event.preventDefault();
      const drawer = cartLink._egjCartDrawer;
      if (drawer && typeof drawer.open === 'function') drawer.open(cartLink);
    };
    cartLink._egjCartKeydown = (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        const drawer = cartLink._egjCartDrawer;
        if (drawer && typeof drawer.open === 'function') drawer.open(cartLink);
      }
    };

    cartLink.addEventListener('click', cartLink._egjCartClick);
    cartLink.addEventListener('keydown', cartLink._egjCartKeydown);
  }

  disconnectedCallback() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink && cartLink._egjCartDrawer === this) {
      cartLink._egjCartDrawer = null;
      delete cartLink.dataset.cartDrawerBound;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.documentElement.classList.add('egj-cart-open');
      document.body.classList.add('egj-cart-open');
    });

    if (this._egjTrapOnOpen) {
      this.removeEventListener('transitionend', this._egjTrapOnOpen);
      clearTimeout(this._egjTrapTimer);
    }
    const trapOnOpen = () => {
      this.removeEventListener('transitionend', trapOnOpen);
      clearTimeout(this._egjTrapTimer);
      this._egjTrapOnOpen = null;
      if (!this.classList.contains('active')) return;
      const emptyContainer = this.querySelector('.drawer__inner-empty');
      const drawerRoot = document.getElementById('CartDrawer') || this.querySelector('.drawer__inner') || this;
      const containerToTrapFocusOn = this.classList.contains('is-empty') && emptyContainer
        ? emptyContainer
        : drawerRoot;
      const focusElement =
        this.querySelector('.drawer__inner') ||
        this.querySelector('.drawer__close') ||
        emptyContainer?.querySelector('a') ||
        containerToTrapFocusOn;

      if (containerToTrapFocusOn && typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    };
    this._egjTrapOnOpen = trapOnOpen;
    this.addEventListener('transitionend', trapOnOpen);
    // transitionend never fires with reduced motion or a hidden tab.
    this._egjTrapTimer = setTimeout(trapOnOpen, 600);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.documentElement.classList.remove('egj-cart-open');
    document.body.classList.remove('egj-cart-open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Host + items own is-empty (EGJ CSS). Clear before section replace so the
    // first ATC from an empty cart reveals header/items/footer immediately.
    this.classList.remove('is-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return;

      const html = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (html === null) return;

      sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      this.bindOverlayClose();
      this.setHeaderCartIconAccessibility();
      if (window.EgjLoading && typeof window.EgjLoading.bindAll === 'function') {
        window.EgjLoading.bindAll(this);
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* Cart line gallery: thumb click swaps primary without touching qty AJAX */
(function bindEgjCartMediaSlider() {
  if (window.__egjCartMediaSliderBound) return;
  window.__egjCartMediaSliderBound = true;

  document.addEventListener('click', (event) => {
    const slide = event.target.closest('[data-egj-cart-slide-src]');
    if (!slide) return;

    const media = slide.closest('[data-egj-cart-media]');
    if (!media) return;

    const src = slide.getAttribute('data-egj-cart-slide-src');
    if (!src) return;

    const primary = media.querySelector('[data-egj-cart-primary]');
    if (primary && primary.getAttribute('src') !== src) {
      primary.setAttribute('src', src);
      primary.classList.remove('is-loaded');
      if (primary.complete) primary.classList.add('is-loaded');
    }

    media.querySelectorAll('[data-egj-cart-slide-src]').forEach((btn) => {
      const active = btn === slide;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  });
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  enableLoading(line) {
    super.enableLoading(line);
    this.classList.add('is-updating');
  }

  disableLoading() {
    super.disableLoading();
    this.classList.remove('is-updating');
    const drawer = this.closest('cart-drawer');
    if (window.EgjLoading && typeof window.EgjLoading.bindAll === 'function') {
      window.EgjLoading.bindAll(drawer || this);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
